import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area, ComposedChart } from 'recharts';
import { CSVLink } from 'react-csv';
import { useTheme } from '../contexts/ThemeContext';
import { useNotifications } from '../contexts/NotificationContext';
import { adminAPI } from '../services/api';
import { getLocalTodayBounds } from '../utils/date';
import { Download, Search, TrendingUp, TrendingDown, Activity, Globe, Clock, Zap } from 'lucide-react';

function Dashboard() {
  const { colors } = useTheme();
  const { addNotification } = useNotifications();
  const adminRole = (localStorage.getItem('adminRole') || 'SUPER_ADMIN').toUpperCase();
  const isSuperAdmin = adminRole === 'SUPER_ADMIN';
  const canAnalytics = ['SUPER_ADMIN', 'ANALYTICS_ADMIN', 'REPORTING_ADMIN'].includes(adminRole);
  const canReports = ['SUPER_ADMIN', 'REPORTING_ADMIN', 'SUPPORT_ADMIN'].includes(adminRole);
  const canModeration = ['SUPER_ADMIN', 'CONTENT_MODERATOR'].includes(adminRole);
  const canVerification = ['SUPER_ADMIN', 'VERIFICATION_ADMIN'].includes(adminRole);
  const canUserMgmt = ['SUPER_ADMIN', 'SUPPORT_ADMIN'].includes(adminRole);
  const canDeleted = ['SUPER_ADMIN', 'COMPLIANCE_AUDITOR'].includes(adminRole);

  // Loading skeleton component
  const LoadingSkeleton = ({ className }) => (
    <div className={`animate-pulse ${colors.card} rounded ${className}`}></div>
  );

  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [userActivity, setUserActivity] = useState(null);
  const [recentReports, setRecentReports] = useState([]);
  const [moderationQueue, setModerationQueue] = useState([]);
  const [geographicData, setGeographicData] = useState([]);
  const [hourlyActivity, setHourlyActivity] = useState([]);
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chartDays, setChartDays] = useState(30);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStats, setFilteredStats] = useState(null);

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    fetchDashboardData();
    
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchDashboardData, 30000); // Refresh every 30 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartDays, autoRefresh, isSuperAdmin]);

  const fetchDashboardData = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      setLoading(true);
      const safeCall = async (fn, fallback = null) => {
        try {
          return await fn();
        } catch (e) {
          if (e?.response?.status === 403) return { data: fallback, __forbidden: true };
          throw e;
        }
      };
      const todayBounds = getLocalTodayBounds();
      const [
        statsResponse,
        analyticsResponse,
        activityResponse,
        verificationStatsResponse,
        photoVerificationsResponse,
        reportsResponse,
        moderationResponse,
        geographicResponse,
        hourlyResponse,
        performanceResponse
      ] = await Promise.all([
        safeCall(() => adminAPI.getDashboardStats(todayBounds), { stats: {} }),
        canAnalytics
          ? safeCall(() => adminAPI.getAnalytics({ days: chartDays }), { analytics: {} })
          : Promise.resolve({ data: { analytics: null } }),
        canAnalytics
          ? safeCall(() => adminAPI.getUserActivity({ days: 7, page: 1, limit: 10 }), { userActivity: null })
          : Promise.resolve({ data: { userActivity: null } }),
        canVerification
          ? safeCall(() => adminAPI.getVerificationStats(), null)
          : Promise.resolve({ data: null }),
        canVerification
          ? safeCall(() => adminAPI.getPendingPhotoVerifications({ limit: 1 }), null)
          : Promise.resolve({ data: null }),
        canReports
          ? safeCall(() => adminAPI.getAllReports({ limit: 5, sort: '-createdAt' }), { reports: [] })
          : Promise.resolve({ data: { reports: [] } }),
        canModeration
          ? safeCall(() => adminAPI.getModerationQueue(), { queue: [] })
          : Promise.resolve({ data: { queue: [] } }),
        canAnalytics
          ? adminAPI.getAnalytics({ days: chartDays, type: 'geographic' }).catch(() => ({ data: { analytics: { geographic: [] } } }))
          : Promise.resolve({ data: { analytics: { geographic: [] } } }),
        canAnalytics
          ? adminAPI.getAnalytics({ days: 1, type: 'hourly' }).catch(() => ({ data: { analytics: { hourly: [] } } }))
          : Promise.resolve({ data: { analytics: { hourly: [] } } }),
        canAnalytics
          ? adminAPI.getAnalytics({ days: 1, type: 'performance' }).catch(() => ({ data: { analytics: { performance: null } } }))
          : Promise.resolve({ data: { analytics: { performance: null } } })
      ]);

      setStats(statsResponse.data.stats);
      setAnalytics(analyticsResponse.data.analytics);
      setUserActivity(activityResponse.data.userActivity);
      setRecentReports(reportsResponse.data.reports || []);
      setModerationQueue(moderationResponse.data.queue || []);
      setGeographicData(geographicResponse.data.analytics?.geographic || []);
      setHourlyActivity(hourlyResponse.data.analytics?.hourly || []);
      setPerformanceMetrics(performanceResponse.data.analytics?.performance || null);
      setLastUpdated(new Date());

      // Check for critical alerts
      const pendingReports = statsResponse.data?.stats?.reports?.pending || 0;
      const pendingVerifications = verificationStatsResponse.data?.pendingReview || 0;

      if (canReports && pendingReports > 10) {
        addNotification('warning', 'High Report Volume', `${pendingReports} reports pending review`);
      }
      if (canVerification && pendingVerifications > 20) {
        addNotification('info', 'Verification Queue', `${pendingVerifications} verifications awaiting review`);
      }

      // Merge verification stats into stats
      if (verificationStatsResponse.data) {
        setStats(prevStats => ({
          ...prevStats,
          verifications: {
            ...verificationStatsResponse.data,
            pending: verificationStatsResponse.data.pendingReview || 0,
          },
        }));
      }

      // Add photo verification count
      if (photoVerificationsResponse.data?.pagination) {
        setStats(prevStats => ({
          ...prevStats,
          photoVerifications: {
            pending: photoVerificationsResponse.data.pagination.total
          }
        }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard data');
      addNotification('error', 'Data Load Error', 'Failed to refresh dashboard data');
    } finally {
      setLoading(false);
    }
  }, [chartDays, addNotification, canAnalytics, canModeration, canReports, canVerification, isSuperAdmin]);

  if (!isSuperAdmin) {
    const roleConfig = getRoleDashboardConfig(adminRole);
    return (
      <div className="space-y-6 max-w-5xl mx-auto">
        <div className={`${colors.gradient} rounded-2xl p-6 text-white shadow-2xl`}>
          <h1 className="text-3xl font-bold mb-2">{roleConfig.title}</h1>
          <p className="text-blue-100">{roleConfig.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {roleConfig.cards.map((card) => (
            <Link
              key={card.to}
              to={card.to}
              className={`${colors.card} rounded-2xl shadow-lg p-5 hover:shadow-xl transition-all duration-200 hover:-translate-y-1 block ${colors.hover}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-2xl">{card.icon}</span>
                <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                  {card.badge}
                </span>
              </div>
              <h3 className={`text-lg font-bold ${colors.text} mb-1`}>{card.title}</h3>
              <p className={`text-sm ${colors.textSecondary}`}>{card.description}</p>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Header Skeleton */}
        <div className="flex justify-between items-center">
          <LoadingSkeleton className="h-10 w-48" />
          <LoadingSkeleton className="h-4 w-32" />
        </div>

        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(7)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <LoadingSkeleton className="w-12 h-12 rounded-xl" />
                <div className="text-right">
                  <LoadingSkeleton className="h-4 w-16 mb-2" />
                  <LoadingSkeleton className="h-6 w-12" />
                </div>
              </div>
              <LoadingSkeleton className="h-4 w-20" />
            </div>
          ))}
        </div>

        {/* Charts Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <LoadingSkeleton className="h-6 w-48 mb-6" />
            <LoadingSkeleton className="h-64 w-full" />
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <LoadingSkeleton className="h-6 w-48 mb-6" />
            <LoadingSkeleton className="h-64 w-full" />
          </div>
        </div>

        {/* Bottom Section Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl shadow-lg p-6">
              <LoadingSkeleton className="h-6 w-32 mb-6" />
              <div className="space-y-3">
                {[...Array(4)].map((_, j) => (
                  <LoadingSkeleton key={j} className="h-12 w-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-lg">
        {error}
      </div>
    );
  }

  // Prepare chart data
  const userGrowthData = analytics?.userGrowth?.slice(-14).map(item => ({
    date: new Date(item._id).toLocaleDateString(),
    users: item.count
  })) || [];

  const messageActivityData = analytics?.messageActivity?.slice(-14).map(item => ({
    date: new Date(item._id).toLocaleDateString(),
    messages: item.count
  })) || [];

  const geographicChartData = geographicData.slice(0, 10).map(item => ({
    country: item._id || 'Unknown',
    users: item.count
  }));

  const hourlyChartData = hourlyActivity.map(item => ({
    hour: `${item._id}:00`,
    activity: item.count
  }));

  const recentActivity = userActivity?.activeUsers?.list?.slice(0, 5) || [];

  // Pie chart data for user verification status
  const verificationData = [
    { name: 'Verified', value: stats?.users?.verified || 0, color: '#10b981' },
    { name: 'Unverified', value: (stats?.users?.totalSignups || 0) - (stats?.users?.verified || 0), color: '#f59e0b' },
  ];

  // Calculate KPI trends
  const calculateTrend = (current, previous) => {
    if (!previous || previous === 0 || !current) return { value: 0, isPositive: true };
    const change = ((current - previous) / previous) * 100;
    const value = isNaN(change) ? 0 : Math.abs(change);
    return { value, isPositive: change >= 0 };
  };

  const signupsToday = stats?.users?.newToday ?? stats?.users?.new24h ?? 0;
  const messagesToday = stats?.messages?.today ?? stats?.messages?.last24h ?? 0;
  const userTrend = calculateTrend(stats?.users?.active || 0, (stats?.users?.active || 0) - signupsToday);
  const messageTrend = calculateTrend(stats?.messages?.total || 0, (stats?.messages?.total || 0) - messagesToday);

  // Export data
  const exportData = [
    {
      metric: 'Total Signups',
      value: stats?.users?.totalSignups || 0,
      change: `+${signupsToday} today`
    },
    {
      metric: 'Active Users',
      value: stats?.users?.active || 0,
      change: `${stats?.users?.online || 0} online now`
    },
    {
      metric: 'Verified Users',
      value: stats?.users?.verified || 0,
      change: `${stats?.users?.verificationRate || 0}% verified`
    },
    {
      metric: 'Total Messages',
      value: stats?.messages?.total || 0,
      change: `+${messagesToday} today`
    },
    {
      metric: 'Pending Reports',
      value: stats?.reports?.pending || 0,
      change: `${stats?.reports?.total || 0} total`
    },
    {
      metric: 'Deleted Accounts',
      value: stats?.deletedAccounts?.total || 0,
      change: `+${stats?.deletedAccounts?.last24h || 0} today`
    }
  ];

  const statCards = [
    {
      title: 'Total Signups',
      value: stats?.users?.totalSignups || 0,
      change: `+${signupsToday} today`,
      icon: '👥',
      color: 'from-blue-500 to-cyan-500',
      trend: userTrend,
    },
    {
      title: 'Active Users',
      value: stats?.users?.active || 0,
      change: `${stats?.users?.online || 0} online now`,
      icon: '🟢',
      color: 'from-green-500 to-emerald-500',
      trend: userTrend,
    },
    {
      title: 'Inactive Users',
      value: stats?.users?.inactive || 0,
      change: 'Last 7 days',
      icon: '⚫',
      color: 'from-gray-500 to-slate-500',
      trend: { value: 0, isPositive: false },
    },
    {
      title: 'Verified Users',
      value: stats?.users?.verified || 0,
      change: `${stats?.users?.verificationRate || 0}% verified`,
      icon: '✅',
      color: 'from-purple-500 to-pink-500',
      trend: { value: stats?.users?.verificationRate || 0, isPositive: true },
    },
    {
      title: 'Total Messages',
      value: stats?.messages?.total || 0,
      change: `+${messagesToday} today`,
      icon: '💬',
      color: 'from-pink-500 to-rose-500',
      trend: messageTrend,
    },
    {
      title: 'Pending Reports',
      value: stats?.reports?.pending || 0,
      change: `${stats?.reports?.total || 0} total`,
      icon: '🚨',
      color: 'from-red-500 to-orange-500',
      trend: { value: 0, isPositive: false },
    },
    {
      title: 'Active Pings',
      value: stats?.pings?.active || 0,
      change: `${stats?.pings?.total || 0} total`,
      icon: '📍',
      color: 'from-yellow-500 to-orange-500',
      trend: { value: 0, isPositive: true },
    },
    {
      title: 'Deleted Accounts',
      value: stats?.deletedAccounts?.total || 0,
      change: `+${stats?.deletedAccounts?.last24h || 0} today · ${stats?.deletedAccounts?.inGracePeriod || 0} in grace`,
      icon: '🗑️',
      color: 'from-slate-600 to-zinc-700',
      trend: { value: 0, isPositive: false },
      link: '/deleted-accounts',
    },
  ];

  const quickActions = [
    canUserMgmt && { to: '/users', icon: '👥', label: 'Manage Users', hover: 'group-hover:text-blue-600' },
    canReports && { to: '/reports', icon: '🚨', label: 'View Reports', hover: 'group-hover:text-red-600' },
    canAnalytics && { to: '/analytics', icon: '📊', label: 'Analytics', hover: 'group-hover:text-green-600' },
    canVerification && { to: '/verifications', icon: '✅', label: 'Verifications', hover: 'group-hover:text-purple-600' },
    canDeleted && { to: '/deleted-accounts', icon: '🗑️', label: 'Deleted Accounts', hover: 'group-hover:text-slate-600' },
    canModeration && { to: '/community-moderation', icon: '🛡️', label: 'Community Mod', hover: 'group-hover:text-pink-600' },
  ].filter(Boolean);

  return (
    <div className="space-y-8 max-w-7xl mx-auto">
      {/* Welcome Header with Controls */}
      <div className={`${colors.gradient} rounded-2xl p-6 text-white shadow-2xl`}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-3xl font-bold mb-2">
              Welcome back, Admin! 👋
            </h1>
            <p className="text-blue-100">
              Here's what's happening with your platform today
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="text-sm text-blue-100">Last updated</div>
              <div className="text-lg font-semibold">
                {lastUpdated.toLocaleTimeString()}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={fetchDashboardData}
                className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                title="Refresh data"
              >
                🔄 Refresh
              </button>
              <label className="flex items-center space-x-2 text-sm">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                  className="rounded"
                />
                <span>Auto-refresh (30s)</span>
              </label>
            </div>
          </div>
        </div>

        {/* Search and Export Bar */}
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search dashboard metrics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/70 focus:outline-none focus:ring-2 focus:ring-white/30"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <CSVLink
              data={exportData}
              filename={`dashboard-export-${new Date().toISOString().split('T')[0]}.csv`}
              className="flex items-center space-x-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </CSVLink>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {quickActions.map((action) => (
          <Link
            key={action.to}
            to={action.to}
            className={`${colors.card} rounded-xl shadow-lg p-4 hover:shadow-xl transition-all duration-200 hover:-translate-y-1 text-center group ${colors.hover}`}
          >
            <div className="text-2xl mb-2">{action.icon}</div>
            <div className={`text-sm font-semibold ${colors.textSecondary} ${action.hover}`}>{action.label}</div>
          </Link>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {statCards.map((card, index) => {
          const cardBody = (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center text-2xl shadow-lg`}>
                  {card.icon}
                </div>
                <div className="text-right">
                  <div className="flex items-center space-x-2 mb-1">
                    <div className="text-xs text-gray-500 uppercase tracking-wide">{card.title}</div>
                    {card.trend && typeof card.trend.value === 'number' && card.trend.value > 0 && (
                      <div className={`flex items-center space-x-1 text-xs px-2 py-1 rounded-full ${
                        card.trend.isPositive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {card.trend.isPositive ? (
                          <TrendingUp className="w-3 h-3" />
                        ) : (
                          <TrendingDown className="w-3 h-3" />
                        )}
                        <span>{card.trend.value.toFixed(1)}%</span>
                      </div>
                    )}
                  </div>
                  <div className={`text-2xl font-bold ${colors.text}`}>{card.value.toLocaleString()}</div>
                </div>
              </div>
              <p className={`text-sm ${colors.textSecondary} font-medium`}>{card.change}</p>
            </>
          );

          const className = `${colors.card} rounded-2xl shadow-lg p-6 ${colors.border} border hover:shadow-xl transition-all duration-200 hover:-translate-y-1 block`;

          return card.link ? (
            <Link key={index} to={card.link} className={className}>
              {cardBody}
            </Link>
          ) : (
            <div key={index} className={className}>
              {cardBody}
            </div>
          );
        })}
      </div>

      {/* Charts Section with Time Range Selector */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Analytics Overview</h2>
          <select
            value={chartDays}
            onChange={(e) => setChartDays(parseInt(e.target.value))}
            className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none text-sm"
          >
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* User Growth Chart */}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
              User Growth Trend
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#6b7280" 
                    fontSize={12}
                    tick={{ fill: '#6b7280' }}
                  />
                  <YAxis 
                    stroke="#6b7280" 
                    fontSize={12}
                    tick={{ fill: '#6b7280' }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="users" 
                    stroke="#3b82f6" 
                    strokeWidth={3}
                    dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: '#3b82f6', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Verification Status Pie Chart */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
              User Verification Status
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={verificationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {verificationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center space-x-4 mt-4">
              {verificationData.map((entry, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className="text-sm text-gray-600">{entry.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Message Activity Chart */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <span className="w-2 h-2 bg-pink-500 rounded-full mr-3"></span>
            Message Activity
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={messageActivityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#6b7280" 
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                />
                <YAxis 
                  stroke="#6b7280" 
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar 
                  dataKey="messages" 
                  fill="#ec4899" 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Advanced Analytics Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Geographic Distribution */}
        <div className={`${colors.card} rounded-2xl shadow-lg p-6`}>
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <Globe className="w-5 h-5 mr-3 text-blue-500" />
            Geographic Distribution
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={geographicChartData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" stroke="#6b7280" fontSize={12} tick={{ fill: '#6b7280' }} />
                <YAxis 
                  type="category" 
                  dataKey="country" 
                  stroke="#6b7280" 
                  fontSize={12} 
                  tick={{ fill: '#6b7280' }}
                  width={80}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Bar 
                  dataKey="users" 
                  fill="#3b82f6" 
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Hourly Activity */}
        <div className={`${colors.card} rounded-2xl shadow-lg p-6`}>
          <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
            <Clock className="w-5 h-5 mr-3 text-green-500" />
            Hourly Activity (Today)
          </h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={hourlyChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="hour" 
                  stroke="#6b7280" 
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                />
                <YAxis 
                  stroke="#6b7280" 
                  fontSize={12}
                  tick={{ fill: '#6b7280' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fff', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Area 
                  type="monotone" 
                  dataKey="activity" 
                  stroke="#10b981" 
                  fill="#10b981" 
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      {performanceMetrics && (
        <div className={`${colors.card} rounded-2xl shadow-lg p-6`}>
          <h2 className={`text-xl font-bold ${colors.text} mb-6 flex items-center`}>
            <Activity className="w-5 h-5 mr-3 text-purple-500" />
            System Performance
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 mb-2">
                {performanceMetrics.avgResponseTime || 0}ms
              </div>
              <div className={`text-sm ${colors.textSecondary}`}>Avg Response Time</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600 mb-2">
                {performanceMetrics.uptime || 0}%
              </div>
              <div className={`text-sm ${colors.textSecondary}`}>System Uptime</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600 mb-2">
                {performanceMetrics.errorRate || 0}%
              </div>
              <div className={`text-sm ${colors.textSecondary}`}>Error Rate</div>
            </div>
          </div>
        </div>
      )}

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Quick Stats */}
        <div className={`${colors.card} rounded-2xl shadow-lg p-6`}>
          <h2 className={`text-xl font-bold ${colors.text} mb-6 flex items-center`}>
            <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
            Platform Stats
          </h2>
          <div className="space-y-4">
            <div className={`flex justify-between items-center p-3 ${colors.hover} rounded-lg`}>
              <span className={`${colors.textSecondary} font-medium`}>Total Albums</span>
              <span className={`font-bold ${colors.text}`}>{stats?.albums?.total || 0}</span>
            </div>
            <div className={`flex justify-between items-center p-3 ${colors.hover} rounded-lg`}>
              <span className={`${colors.textSecondary} font-medium`}>Shared Albums</span>
              <span className={`font-bold ${colors.text}`}>{stats?.albums?.shared || 0}</span>
            </div>
            <div className={`flex justify-between items-center p-3 ${colors.hover} rounded-lg`}>
              <span className={`${colors.textSecondary} font-medium`}>Active Discovery</span>
              <span className={`font-bold ${colors.text}`}>{stats?.discovery?.active || 0}</span>
            </div>
            <div className={`flex justify-between items-center p-3 ${colors.hover} rounded-lg`}>
              <span className={`${colors.textSecondary} font-medium`}>Total Discovery</span>
              <span className={`font-bold ${colors.text}`}>{stats?.discovery?.total || 0}</span>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className={`${colors.card} rounded-2xl shadow-lg p-6`}>
          <h2 className={`text-xl font-bold ${colors.text} mb-6 flex items-center`}>
            <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
            Recent Activity
          </h2>
          <div className="space-y-3">
            {recentActivity.length > 0 ? recentActivity.map((user, index) => (
              <div key={index} className={`flex items-center justify-between p-3 ${colors.hover} rounded-lg transition-colors`}>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {user.username?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${colors.text}`}>{user.username || 'Unknown'}</p>
                    <p className="text-xs text-gray-500">
                      {user.lastActivity ? new Date(user.lastActivity).toLocaleDateString() : 'No activity'}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-gray-500">
                  {user.isOnline ? '🟢 Online' : '⚫ Offline'}
                </div>
              </div>
            )) : (
              <p className={`${colors.textSecondary} text-center py-4`}>No recent activity</p>
            )}
          </div>
          <div className="mt-4">
            <Link 
              to="/user-activity" 
              className={`text-sm text-blue-600 hover:text-blue-800 font-medium hover:underline`}
            >
              View all activity →
            </Link>
          </div>
        </div>

        {/* Recent Reports */}
        <div className={`${colors.card} rounded-2xl shadow-lg p-6`}>
          <h2 className={`text-xl font-bold ${colors.text} mb-6 flex items-center`}>
            <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
            Recent Reports
          </h2>
          <div className="space-y-3">
            {recentReports.length > 0 ? recentReports.slice(0, 4).map((report, index) => (
              <div key={index} className="p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-red-700 uppercase">
                    {report.type || 'Report'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(report.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-gray-700 truncate">
                  {report.description || 'Reported content'}
                </p>
              </div>
            )) : (
              <p className={`${colors.textSecondary} text-center py-4`}>No recent reports</p>
            )}
          </div>
          <div className="mt-4">
            <Link 
              to="/reports" 
              className="text-sm text-red-600 hover:text-red-800 font-medium hover:underline"
            >
              View all reports →
            </Link>
          </div>
        </div>

        {/* Pending Actions & Alerts */}
        <div className={`${colors.card} rounded-2xl shadow-lg p-6`}>
          <h2 className={`text-xl font-bold ${colors.text} mb-6 flex items-center`}>
            <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
            Action Items
          </h2>
          <div className="space-y-4">
            <Link 
              to="/reports" 
              className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors group"
            >
              <div className="flex items-center space-x-3">
                <span className="text-red-500">🚨</span>
                <span className="text-gray-700 font-medium">Pending Reports</span>
              </div>
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                {stats?.reports?.pending || 0}
              </span>
            </Link>
            
            <Link 
              to="/verifications" 
              className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors group"
            >
              <div className="flex items-center space-x-3">
                <span className="text-yellow-500">✅</span>
                <span className="text-gray-700 font-medium">Pending Verifications</span>
              </div>
              <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                {stats?.verifications?.pending || 0}
              </span>
            </Link>

            <Link 
              to="/photo-verifications" 
              className="flex items-center justify-between p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors group"
            >
              <div className="flex items-center space-x-3">
                <span className="text-blue-500">📸</span>
                <span className="text-gray-700 font-medium">Photo Verifications</span>
              </div>
              <span className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                {stats?.photoVerifications?.pending || 0}
              </span>
            </Link>

            <Link
              to="/reports"
              className="flex items-center justify-between p-3 bg-red-50 rounded-lg hover:bg-red-100 transition-colors group"
            >
              <div className="flex items-center space-x-3">
                <span className="text-red-500">🛡️</span>
                <span className="text-gray-700 font-medium">Safety queue</span>
              </div>
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full font-bold">
                {stats?.reports?.safetyPendingHigh ?? stats?.reports?.safetyPending ?? 0}
              </span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;

function getRoleDashboardConfig(role) {
  const base = {
    title: 'Admin Dashboard',
    subtitle: 'Use your role-specific tools below.',
    cards: [],
  };

  const configs = {
    CONTENT_MODERATOR: {
      title: 'Content Moderation Dashboard',
      subtitle: 'Review content quality, moderation queues, and communication tools.',
      cards: [
        {
          to: '/community-moderation',
          icon: '🛡️',
          badge: 'Core',
          title: 'Community Moderation',
          description: 'Scan, review, remove, and restore community posts.',
        },
        {
          to: '/hidden-users',
          icon: '👻',
          badge: 'Safety',
          title: 'Hidden Users',
          description: 'Hide/unhide accounts from discover and nearby.',
        },
        {
          to: '/app-promotions',
          icon: '🎉',
          badge: 'Campaigns',
          title: 'App Promotions',
          description: 'Manage promotion cards and campaign visibility.',
        },
        {
          to: '/push-notifications',
          icon: '🔔',
          badge: 'Broadcast',
          title: 'Push Notifications',
          description: 'Send targeted broadcast notifications to users.',
        },
      ],
    },
    REPORTING_ADMIN: {
      title: 'Reporting Dashboard',
      subtitle: 'Handle safety/content reports and user contact issues.',
      cards: [
        { to: '/reports', icon: '🚨', badge: 'Core', title: 'Reports', description: 'Review and resolve safety/content reports.' },
        { to: '/contacts', icon: '📧', badge: 'Support', title: 'Contacts', description: 'Handle user contact submissions.' },
        { to: '/analytics', icon: '📈', badge: 'Insights', title: 'Analytics', description: 'Track report trends and platform data.' },
        { to: '/impressions', icon: '👀', badge: 'Insights', title: 'Impressions', description: 'Monitor app tab usage and engagement.' },
      ],
    },
    COMPLIANCE_AUDITOR: {
      title: 'Compliance Dashboard',
      subtitle: 'Review deleted-account archives and legal audit trails.',
      cards: [
        {
          to: '/deleted-accounts',
          icon: '🗑️',
          badge: 'Legal',
          title: 'Deleted Accounts',
          description: 'Inspect archives and legal audit traces post-purge.',
        },
      ],
    },
    ANALYTICS_ADMIN: {
      title: 'Analytics Dashboard',
      subtitle: 'Track growth, usage, and regional performance.',
      cards: [
        { to: '/analytics', icon: '📊', badge: 'Core', title: 'Analytics', description: 'View core business and user metrics.' },
        { to: '/impressions', icon: '👀', badge: 'Engagement', title: 'Impressions', description: 'Daily tab-level engagement insights.' },
        { to: '/regional-insights', icon: '🌍', badge: 'Geo', title: 'Regional Insights', description: 'Analyze performance by geography.' },
        { to: '/user-activity', icon: '🔥', badge: 'Behavior', title: 'User Activity', description: 'Inspect activity and retention signals.' },
      ],
    },
    VERIFICATION_ADMIN: {
      title: 'Verification Dashboard',
      subtitle: 'Manage profile and photo verification workflows.',
      cards: [
        { to: '/verifications', icon: '✅', badge: 'Core', title: 'Verifications', description: 'Review verification applications.' },
        { to: '/verified-users', icon: '🛡️', badge: 'List', title: 'Verified Users', description: 'Browse and inspect verified users.' },
        { to: '/photo-verifications', icon: '📷', badge: 'Photos', title: 'Photo Verifications', description: 'Approve/reject photo verifications.' },
      ],
    },
    SUPPORT_ADMIN: {
      title: 'Support Dashboard',
      subtitle: 'Manage users, reports, and support workflows.',
      cards: [
        { to: '/users', icon: '👥', badge: 'Core', title: 'Users', description: 'Search and manage user accounts.' },
        { to: '/reports', icon: '🚨', badge: 'Safety', title: 'Reports', description: 'Handle reports and moderation status.' },
        { to: '/contacts', icon: '📧', badge: 'Support', title: 'Contacts', description: 'Respond to user contact requests.' },
        { to: '/photo-verifications', icon: '📷', badge: 'Queue', title: 'Photo Verifications', description: 'Handle pending photo checks.' },
      ],
    },
  };

  return configs[role] || base;
}

